import type { ReactNode } from 'react';
import { I, sx } from './kit';

/** A choice inside a rule row (the design's pick()/multi() options, L22926). */
export interface RuleOpt {
  label: string;
  on: boolean;
  onClick?: () => void;
  /** Extra style (Critical is locked on). */
  extra?: string;
}

/** A read-only or choosable rule row (row(), L22931), as On-call, Voice & Scribe, Guardian rules and the "always" lists draw them. */
export interface RuleRow {
  label: string;
  sub: string;
  opts?: RuleOpt[];
  tag?: string;
  tagStyle?: string;
}

export const optStyle = (on: boolean) =>
  'display:inline-flex;align-items:center;height:26px;padding:0 10px;border-radius:6px;font-size:16px;text-decoration:none;white-space:nowrap;' +
  (on ? 'background:#2F5C97;color:#fff;font-weight:600;' : 'background:#F7F3EA;color:#3C454F;border:1px solid rgba(34,39,46,.16);');

export function Opt({ o, ro }: { o: RuleOpt; ro: string | null }) {
  return (
    <a
      href="#"
      aria-pressed={o.on}
      aria-disabled={ro ? true : undefined}
      title={ro ?? undefined}
      onClick={(e) => {
        e.preventDefault();
        if (!ro && o.onClick) o.onClick();
      }}
      style={sx(optStyle(o.on) + (o.extra ?? ''))}
    >
      {I(o.label)}
    </a>
  );
}

/** Rows with a 10px pad (On-call, Voice & Scribe, Guardian rules) or 8/5px (the "always" lists). */
export function RuleRows({ rows, pad = '10px 0', ro, plain, children }: { rows: RuleRow[]; pad?: string; ro: string | null; plain?: boolean; children?: ReactNode }) {
  return (
    <>
      {rows.map((r) => (
        <div key={r.label} style={sx('display:flex;align-items:center;gap:8px 16px;flex-wrap:wrap;padding:' + pad + ';border-top:1px solid rgba(34,39,46,.08);')}>
          <span style={sx('flex:1 1 260px;min-width:0;')}>
            <span style={sx('display:block;font-size:16px;font-weight:700;color:#22272E;')}>{I(r.label)}</span>
            <span style={sx('display:block;font-size:16px;color:#54606C;margin-top:2px;text-wrap:pretty;')}>{I(r.sub)}</span>
          </span>
          {plain ? (
            <span style={sx(r.tagStyle ?? '')}>{I(r.tag ?? '')}</span>
          ) : (
            <span style={sx('flex:0 1 auto;display:flex;align-items:center;justify-content:flex-end;gap:6px;flex-wrap:wrap;')}>
              {(r.opts ?? []).map((o) => (
                <Opt key={o.label} o={o} ro={ro} />
              ))}
              {r.tag && <span style={sx(r.tagStyle ?? '')}>{I(r.tag)}</span>}
            </span>
          )}
        </div>
      ))}
      {children}
    </>
  );
}
